import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, mergeMap, switchMap } from 'rxjs/operators';

import { injectCatchHttpError } from '../../../../../core/errors/catch-http-error.util';
import type { IProjectTag } from '../../../../models';
import { BoardTypes } from '../../../../models';
import { ApiProjectsService } from '../../../../services/api/api-projects.service';
import {
  getBoardIssues,
  getBoardIssuesFail,
  getBoardIssuesSuccess,
  getTagBoards,
  getTagBoardsFail,
  getTagBoardsSuccess,
} from './board.actions';

@Injectable()
export class BoardEffects {
  private apiProjects = inject(ApiProjectsService);

  private readonly actions$ = inject<Actions>(Actions);
  private readonly catchHttpError = injectCatchHttpError();

  getTagBoards$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getTagBoards),
      switchMap(({ requestParams, shouldAddToList }) =>
        this.apiProjects.getProjectTags({ ...requestParams, pagination: 0 }).pipe(
          map(({ count, next, previous, results }) => {
            const response = {
              count,
              next,
              previous,
              results: results.map((tag: IProjectTag) => ({
                type: BoardTypes.Tag,
                projectTag: tag,
              })),
            };

            return getTagBoardsSuccess({ response, shouldAddToList });
          }),
          this.catchHttpError(() => getTagBoardsFail()),
        ),
      ),
    ),
  );

  getBoardIssues$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getBoardIssues),
      mergeMap(({ requestParams, board, shouldAddToList }) =>
        this.apiProjects.getIssues(requestParams).pipe(
          map(response => getBoardIssuesSuccess({ response, board, shouldAddToList })),
          this.catchHttpError(() => getBoardIssuesFail({ board })),
        ),
      ),
    ),
  );
}
